import { useState } from "react"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FormField } from "@/components/ui/formField"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { CheckCircle2, Package, ShieldAlert, X } from "lucide-react"
import { Link, useNavigate } from "@tanstack/react-router"
import { QUALIFICATION_TYPES, type FinalStatus, type ServiceType, parseAttachment } from "@/Wist-blockchain/collections/serviceReport/types"
import { parseAdditionalEquipment, type Instrument } from "@/Wist-blockchain/collections/instruments/types"
import type { Company } from "@/lib/collections/organizations/getOrganizationsFetch"
import { ActivitiesTable } from "../../components/activities-table"
import { MaterialsTable } from "../../components/materials-table"
import { ReportFilesUploader } from "../../components/anexos-files-uploader"
import { GenericListManager } from "@/components/ui/generic-list-manager"
import { InfoItem } from "../../components/info-item"
import { useServiceReportForm, type RevisionMode } from "../../utils/useServiceReportForm"
import { DatePicker } from "../../components/date-picker"
import type { ServiceReport } from "@/Wist-blockchain/collections/serviceReport/types"
import type { Models } from "appwrite"

const SERVICE_TYPES: { value: ServiceType; label: string }[] = [
    { value: "instalacion", label: "Instalación" },
    { value: "preventivo", label: "Preventivo" },
    { value: "correctivo", label: "Correctivo" },
    { value: "calificacion-validacion", label: "Calificación / Validación" },
    { value: "capacitacion", label: "Capacitación" },
    { value: "otro", label: "Otro" },
]

const SERVICE_REASONS = [
    { value: "instalacion", label: "Instalación" },
    { value: "mantenimiento-programado", label: "Mantenimiento programado" },
    { value: "falla-reportada", label: "Falla reportada" },
    { value: "revision-anomalia", label: "Revisión por anomalía" },
    { value: "otro", label: "Otro" },
]

const FINAL_STATUSES: FinalStatus[] = ["operativo", "operativo_observaciones", "fuera_servicio"]

const FINAL_STATUS_CONFIG: Record<FinalStatus, { label: string; className: string; activeClassName: string }> = {
    operativo: {
        label: "Operativo",
        className: "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400 cursor-pointer",
        activeClassName: "border-emerald-500 bg-emerald-100 text-emerald-800 ring-2 ring-emerald-500/30 dark:bg-emerald-950 dark:text-emerald-300 cursor-pointer",
    },
    operativo_observaciones: {
        label: "Operativo con observaciones",
        className: "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400 cursor-pointer",
        activeClassName: "border-amber-500 bg-amber-100 text-amber-800 ring-2 ring-amber-500/30 dark:bg-amber-950 dark:text-amber-300 cursor-pointer",
    },
    fuera_servicio: {
        label: "Fuera de servicio",
        className: "border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400 cursor-pointer",
        activeClassName: "border-red-500 bg-red-100 text-red-800 ring-2 ring-red-500/30 dark:bg-red-950 dark:text-red-300 cursor-pointer",
    },
}

const SECTIONS = [
    { id: 1, label: "General" }, { id: 2, label: "Cliente" }, { id: 3, label: "Equipo" },
    { id: 4, label: "Diagnóstico" }, { id: 5, label: "Actividades" }, { id: 6, label: "Materiales" },
    { id: 7, label: "Resultados" }, { id: 8, label: "Cierre" },
]

//  Sub-components 

function SectionCard({ step, title, description, children }: {
    step: number; title: string; description: string; children: React.ReactNode
}) {
    return (
        <Card id={`section-${step}`} className="border-border/50 shadow-none rounded-xl">
            <CardHeader className="pb-3 pt-5 px-5">
                <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded bg-gray-100 dark:bg-gray-900 text-primary flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5 border border-primary/20">
                        {step}
                    </div>
                    <div>
                        <CardTitle className="text-sm font-semibold leading-tight">{title}</CardTitle>
                        <CardDescription className="text-xs mt-0.5">{description}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4 px-5 pb-5">{children}</CardContent>
        </Card>
    )
}

// Props 

interface AddServiceReportProps {
    instrument?: Instrument
    company?: Company
    draftId?: string
    initialDraft?: ServiceReport
    initialActivities?: Models.Document[]
    initialMaterials?: Models.Document[]
    revisionMode?: RevisionMode
}

// Componente 

const AddServiceReport = ({ instrument, company, draftId, initialDraft, initialActivities, initialMaterials, revisionMode }: AddServiceReportProps) => {
    const navigate = useNavigate()
    const [activeSection, setActiveSection] = useState<number | null>(null)
    const [timeError, setTimeError] = useState("")
    const [maintenancePreset, setMaintenancePreset] = useState<"3m" | "6m" | "1y" | null>(null)
    const [qualificationPreset, setQualificationPreset] = useState<"3m" | "6m" | "1y" | null>(null)
    const [showCancelDialog, setShowCancelDialog] = useState(false)
    const [duration, setDuration] = useState(() => {
        const start = initialDraft?.serviceStartTime
        const end = initialDraft?.serviceEndTime
        if (!start || !end) return ""
        const entry = start.split("T")[1]?.slice(0, 5) ?? ""
        const exit = end.split("T")[1]?.slice(0, 5) ?? ""
        if (!entry || !exit) return ""
        const [eh, em] = entry.split(":").map(Number)
        const [xh, xm] = exit.split(":").map(Number)
        const totalMin = (xh * 60 + xm) - (eh * 60 + em)
        if (totalMin <= 0) return ""
        const h = Math.floor(totalMin / 60)
        const m = totalMin % 60
        return h > 0 ? `${h}h ${m}m` : `${m}m`
    })

    const validateTimes = (entry: string, exit: string) => {
        if (!entry || !exit) { setTimeError(""); setDuration(""); return }
        const [eh, em] = entry.split(":").map(Number)
        const [xh, xm] = exit.split(":").map(Number)
        const totalMin = (xh * 60 + xm) - (eh * 60 + em)
        if (totalMin <= 0) {
            setTimeError("La hora de salida debe ser posterior a la hora de entrada")
            setDuration("")
        } else {
            setTimeError("")
            const h = Math.floor(totalMin / 60)
            const m = totalMin % 60
            setDuration(h > 0 ? `${h}h ${m}m` : `${m}m`)
        }
    }

    const {
        report, setField,
        activities, addActivity, removeActivity, updateActivity,
        materials, addMaterial, removeMaterial, updateMaterial, totalMaterials,
        pendingFiles, setPendingFiles,
        removeExistingAttachment, updateExistingDescription,
        engineerLabel, saving, savingDraft,
        handleSubmit, handleSaveDraft,
        formErrors,
        previousServiceInfo,
        hasUnsavedChanges,
    } = useServiceReportForm(instrument, company, draftId, initialDraft, initialActivities, initialMaterials, revisionMode)

    const scrollToSection = (id: number) => {
        document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })
        setActiveSection(id)
    }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <Package className="h-4 w-4" />
                        <BreadcrumbLink asChild>
                            <Link
                                to="/instruments/$id"
                                params={{ id: instrument?.$id ?? "" }}
                            >
                                {instrument?.equipmentName ?? "Equipo"}
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>
                            {revisionMode
                                ? `Revisión REV${revisionMode.nextRevisionNumber}`
                                : draftId ? "Continuar borrador" : "Nuevo Informe"}
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="w-full min-h-screen flex flex-col mt-4">
                <div className="flex-1 w-full">
                    <div className="w-full max-w-5xl mx-auto space-y-4">

                        {/* Sticky header */}
                        <div className="sticky top-0 z-50 shrink-0 bg-background/95 backdrop-blur-sm border-b border-border/50 rounded-2xl">
                            <div className="px-4 sm:px-6 h-11 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium truncate">
                                        {revisionMode
                                            ? "Creando revisión de informe"
                                            : draftId ? "Continuando borrador" : "Registro de Informe de Servicio"}
                                    </span>
                                    {revisionMode && (
                                        <Badge className="bg-amber-100 text-amber-700 border-amber-300 font-mono text-xs shrink-0">
                                            REV{revisionMode.nextRevisionNumber}
                                        </Badge>
                                    )}
                                </div>
                                {report.reportCode && (
                                    <Badge variant="outline" className="font-mono text-xs text-primary border-primary/30 shrink-0">
                                        {report.reportCode}
                                    </Badge>
                                )}
                            </div>
                            <div className="border-t border-border/30 flex overflow-x-auto scrollbar-hide">
                                {SECTIONS.map((s) => (
                                    <button key={s.id} type="button" onClick={() => scrollToSection(s.id)}
                                        className={`flex items-center gap-1.5 px-3 sm:px-5 py-2 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 transition-all cursor-pointer flex-1 justify-center sm:flex-none sm:justify-start ${activeSection === s.id ? "text-primary border-primary" : "text-muted-foreground border-transparent hover:text-foreground"}`}>
                                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors ${activeSection === s.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                                            {s.id}
                                        </span>
                                        <span className="hidden sm:block">{s.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/*  General */}
                        <SectionCard step={1} title="Datos generales del servicio" description="Datos de trazabilidad y programación del servicio">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="hidden">
                                    <Input readOnly value={report.reportCode} />
                                </div>
                                <FormField label="Fecha de Servicio" required error={formErrors.serviceDate}>
                                    <DatePicker
                                        value={report.serviceDate}
                                        onChange={v => setField("serviceDate", v)}
                                        placeholder="Seleccionar fecha..."
                                        disableFuture
                                        maxDaysBack={5}
                                    />
                                </FormField>
                            </div>
                            <Separator className="opacity-40" />
                            <FormField label="Tipo de Servicio" required error={formErrors.serviceTypes}>
                                <div className="flex flex-wrap gap-2">
                                    {SERVICE_TYPES.map(t => {
                                        const isActive = report.serviceTypes.includes(t.value)
                                        return (
                                            <button key={t.value} type="button"
                                                onClick={() => {
                                                    const updated = isActive
                                                        ? report.serviceTypes.filter(v => v !== t.value)
                                                        : [...report.serviceTypes, t.value]
                                                    setField("serviceTypes", updated as ServiceType[])
                                                    if (isActive && t.value === "calificacion-validacion") {
                                                        setField("qualificationTypes", [])
                                                        setField("nextQualificationDate", "")
                                                    }
                                                }}
                                                className={`px-4 py-2 rounded-md text-xs font-semibold tracking-wide border transition-all cursor-pointer ${isActive ? "border-primary bg-primary/10 text-primary ring-2 ring-primary/20 dark:bg-primary/20" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}>
                                                {t.label}
                                            </button>
                                        )
                                    })}
                                </div>
                            </FormField>
                            {report.serviceTypes.includes("calificacion-validacion") && (
                                <>
                                    <FormField label="Tipos de Calificación">
                                        <div className="flex flex-wrap gap-2">
                                            {QUALIFICATION_TYPES.map(q => {
                                                const isActive = report.qualificationTypes.includes(q.value)
                                                return (
                                                    <button key={q.value} type="button"
                                                        onClick={() => {
                                                            const updated = isActive
                                                                ? report.qualificationTypes.filter(v => v !== q.value)
                                                                : [...report.qualificationTypes, q.value]
                                                            setField("qualificationTypes", updated)
                                                        }}
                                                        className={`px-4 py-2 rounded-md text-xs font-semibold tracking-wide border transition-all cursor-pointer ${isActive ? "border-violet-500 bg-violet-100 text-violet-800 ring-2 ring-violet-500/30 dark:bg-violet-950 dark:text-violet-300" : "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-400"}`}>
                                                        {q.label}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </FormField>
                                    <FormField label="Próxima Calificación">
                                        <div className="space-y-2">
                                            <div className="grid grid-cols-3 gap-2">
                                                {([
                                                    { label: "3 meses", sublabel: "+90 días", preset: "3m", months: 3, active: "border-emerald-500 bg-emerald-100 text-emerald-800 ring-2 ring-emerald-500/30 dark:bg-emerald-950 dark:text-emerald-300", idle: "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400", sub: { active: "text-emerald-600 dark:text-emerald-500", idle: "text-emerald-500/60 dark:text-emerald-600" } },
                                                    { label: "6 meses", sublabel: "+180 días", preset: "6m", months: 6, active: "border-blue-500 bg-blue-100 text-blue-800 ring-2 ring-blue-500/30 dark:bg-blue-950 dark:text-blue-300", idle: "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400", sub: { active: "text-blue-600 dark:text-blue-500", idle: "text-blue-500/60 dark:text-blue-600" } },
                                                    { label: "1 año", sublabel: "+365 días", preset: "1y", months: 12, active: "border-violet-500 bg-violet-100 text-violet-800 ring-2 ring-violet-500/30 dark:bg-violet-950 dark:text-violet-300", idle: "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-400", sub: { active: "text-violet-600 dark:text-violet-500", idle: "text-violet-500/60 dark:text-violet-600" } },
                                                ] as const).map(({ label, sublabel, preset, months, active, idle, sub }) => (
                                                    <button key={preset} type="button"
                                                        onClick={() => {
                                                            const d = new Date()
                                                            d.setMonth(d.getMonth() + months)
                                                            setField("nextQualificationDate", d.toISOString().split("T")[0])
                                                            setQualificationPreset(preset)
                                                        }}
                                                        className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-md border font-semibold tracking-wide transition-all cursor-pointer ${qualificationPreset === preset ? active : idle}`}>
                                                        <span className="text-xs">{label}</span>
                                                        <span className={`text-[10px] font-normal tracking-normal ${qualificationPreset === preset ? sub.active : sub.idle}`}>{sublabel}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            <DatePicker
                                                value={report.nextQualificationDate}
                                                onChange={v => { setField("nextQualificationDate", v); setQualificationPreset(null) }}
                                                placeholder="Fecha personalizada..."
                                                disablePast
                                            />
                                        </div>
                                    </FormField>
                                </>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <FormField label="Hora de Entrada">
                                    <Input
                                        type="time"
                                        step="1"
                                        value={report.entryTime}
                                        onChange={e => {
                                            setField("entryTime", e.target.value)
                                            validateTimes(e.target.value, report.exitTime)
                                        }}
                                        className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    />
                                </FormField>
                                <FormField label="Hora de Salida" error={timeError}>
                                    <Input
                                        type="time"
                                        step="1"
                                        value={report.exitTime}
                                        onChange={e => {
                                            setField("exitTime", e.target.value)
                                            validateTimes(report.entryTime, e.target.value)
                                        }}
                                        className={`bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer${timeError ? " border-red-500" : ""}`}
                                    />
                                </FormField>
                                <FormField label="Duración total">
                                    <Input readOnly value={duration || "—"} className="bg-muted text-muted-foreground cursor-not-allowed" />
                                </FormField>
                                <FormField label="Orden de Compra">
                                    <Input placeholder="OC-XXXX" value={report.purchaseOrder} onChange={e => setField("purchaseOrder", e.target.value)} />
                                </FormField>
                            </div>
                            <Separator className="opacity-40" />
                            <FormField label="Ingeniero Responsable" required>
                                <Input readOnly value={engineerLabel} className="bg-muted text-muted-foreground cursor-not-allowed sm:max-w-sm" />
                            </FormField>
                        </SectionCard>

                        {/* 2. Cliente */}
                        <SectionCard step={2} title="Datos del Cliente" description="Información de la empresa y contacto en sitio">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <FormField label="Nombre Comercial">
                                    <Input placeholder="Nombre comercial..." value={report.clientComercialName} onChange={e => setField("clientComercialName", e.target.value)} />
                                </FormField>
                                <FormField label="Razón Social">
                                    <Input placeholder="Razón social de la empresa" value={report.clientName} onChange={e => setField("clientName", e.target.value)} />
                                </FormField>
                                <FormField label="Ciudad">
                                    <Input placeholder="Ciudad de la empresa" value={report.clientCity} onChange={e => setField("clientCity", e.target.value)} />
                                </FormField>
                                <FormField label="RUC / Identificación Fiscal">
                                    <Input placeholder="20XXXXXXXXXX1" value={report.clientRuc} onChange={e => setField("clientRuc", e.target.value)} />
                                </FormField>
                                <FormField label="Dirección">
                                    <Input placeholder="Av. Industrial 123" value={report.clientAddress} onChange={e => setField("clientAddress", e.target.value)} />
                                </FormField>
                            </div>
                            <Separator className="opacity-40" />
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <FormField label="Contacto Técnico">
                                    <Input value={report.clientContact} onChange={e => setField("clientContact", e.target.value)} />
                                </FormField>
                                <FormField label="Cargo">
                                    <Input value={report.clientPosition} onChange={e => setField("clientPosition", e.target.value)} />
                                </FormField>
                                <FormField label="Área / Laboratorio">
                                    <Input value={report.clientArea} onChange={e => setField("clientArea", e.target.value)} />
                                </FormField>
                                <FormField label="Teléfono">
                                    <Input value={report.clientPhone} onChange={e => setField("clientPhone", e.target.value)} />
                                </FormField>
                                <FormField label="Correo" error={formErrors.clientEmail}>
                                    <Input value={report.clientEmail} type="email" onChange={e => setField("clientEmail", e.target.value)} placeholder="correo@empresa.com" />
                                </FormField>
                            </div>
                        </SectionCard>

                        {/* 3. Equipo */}
                        <SectionCard step={3} title="Equipo Inspeccionado" description="Información del equipo registrada en el sistema">
                            <div className="space-y-4">
                                <div className="rounded-lg border p-4">
                                    <h4 className="text-sm font-semibold mb-3">Identificación</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <InfoItem label="Nombre del equipo" value={instrument?.equipmentName} />
                                        <InfoItem label="Modelo" value={instrument?.model} />
                                        <InfoItem label="N.° Serie" value={instrument?.serialNumber} />
                                        <InfoItem label="Marca" value={instrument?.idBrand} />
                                    </div>
                                </div>
                                <div className="rounded-lg border p-4">
                                    <h4 className="text-sm font-semibold mb-3">Datos Técnicos</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <InfoItem label="Voltaje" value={instrument?.voltage} />
                                        <InfoItem label="Corriente" value={instrument?.current} />
                                        <InfoItem label="Año fabricación" value={instrument?.manufactureYear} />
                                        <InfoItem label="Antigüedad" value={`${new Date().getFullYear() - Number(instrument?.manufactureYear)} años`} />
                                    </div>
                                </div>
                                <div className="rounded-lg border p-4">
                                    <h4 className="text-sm font-semibold mb-3">Control y Ubicación</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <InfoItem label="Código Activo cliente" value={instrument?.clientAssetCode} />
                                        <InfoItem label="Ubicación" value={instrument?.location} />
                                        <InfoItem label="Estado" value={instrument?.status} />
                                        <InfoItem
                                            label="Servicio previo WIST"
                                            value={previousServiceInfo.isFirst
                                                ? "Primera visita"
                                                : `Servicio anterior — ${previousServiceInfo.lastDate
                                                    ? new Date(previousServiceInfo.lastDate).toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" })
                                                    : "—"}`
                                            }
                                        />
                                    </div>
                                </div>
                                {instrument?.equipmentDescription && (
                                    <div className="rounded-lg border p-4">
                                        <h4 className="text-sm font-semibold mb-2">Descripción</h4>
                                        <p className="text-sm text-muted-foreground">{instrument.equipmentDescription}</p>
                                    </div>
                                )}
                                {instrument?.additionalEquipment && instrument.additionalEquipment.length > 0 && (
                                    <div className="rounded-lg border p-4">
                                        <h4 className="text-sm font-semibold mb-3">Equipos Adicionales</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {parseAdditionalEquipment(instrument.additionalEquipment).map((item, i) => (
                                                <Badge key={i} variant="outline" className="text-xs">
                                                    {item.nombre}{item.modelo ? ` — ${item.modelo}` : ""}{item.serial ? ` (S/N: ${item.serial})` : ""}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </SectionCard>

                        {/* 4. Diagnóstico */}
                        <SectionCard step={4} title="Motivo del servicio" description="Causa del servicio y parámetros técnicos verificados">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <FormField label="Motivo del Servicio" required error={formErrors.serviceReason}>
                                    <Select value={report.serviceReason} onValueChange={v => setField("serviceReason", v)}>
                                        <SelectTrigger className="cursor-pointer w-full"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                                        <SelectContent>
                                            {SERVICE_REASONS.map(r => <SelectItem key={r.value} value={r.value} className="cursor-pointer">{r.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Descripción Detallada del Motivo">
                                    <Textarea placeholder="Explique la situación encontrada al inicio del servicio..."
                                        value={report.reasonDescription} onChange={e => setField("reasonDescription", e.target.value)}
                                        rows={3} className="resize-none" />
                                </FormField>
                            </div>
                        </SectionCard>

                        {/* 5. Actividades */}
                        <SectionCard step={5} title="Actividades Realizadas" description="Tabla de actividades ejecutadas durante el servicio">
                            <ActivitiesTable activities={activities} onAdd={addActivity} onRemove={removeActivity} onUpdate={updateActivity} />
                        </SectionCard>

                        {/* 6. Materiales */}
                        <SectionCard step={6} title="Materiales y Repuestos" description="Partes utilizadas durante la intervención">
                            <MaterialsTable materials={materials} onAdd={addMaterial} onRemove={removeMaterial} onUpdate={updateMaterial} total={totalMaterials} />
                        </SectionCard>

                        {/* 7. Resultados */}
                        <SectionCard step={7} title="Resultados del servicio" description="Estado final y recomendaciones">
                            <FormField label="Estado Final del Equipo" required error={formErrors.finalStatus}>
                                <div className="flex flex-wrap gap-2">
                                    {FINAL_STATUSES.map(s => {
                                        const config = FINAL_STATUS_CONFIG[s]
                                        return (
                                            <button key={s} type="button" onClick={() => setField("finalStatus", s)}
                                                className={`px-4 py-2 rounded-md text-xs font-semibold tracking-wide border transition-all ${report.finalStatus === s ? config.activeClassName : config.className}`}>
                                                {config.label}
                                            </button>
                                        )
                                    })}
                                </div>
                            </FormField>
                            <GenericListManager
                                items={report.verifiedParameters}
                                onChange={v => setField("verifiedParameters", v)}
                                label="Parámetros Verificados"
                                placeholder="Describe el parámetro verificado..."
                                emptyMessage="Sin parámetros registrados"
                                addButtonText="Agregar parámetro"
                                singularLabel="parámetro"
                                pluralLabel="parámetros"
                            />
                            <Separator className="opacity-40" />
                            <GenericListManager
                                items={report.recommendations}
                                onChange={v => setField("recommendations", v)}
                                label="Recomendaciones"
                                placeholder="Indicaciones para el cliente o próximo técnico..."
                                emptyMessage="Sin recomendaciones registradas"
                                addButtonText="Agregar recomendación"
                                singularLabel="recomendación"
                                pluralLabel="recomendaciones"
                            />
                            <FormField label="Próximo Mantenimiento">
                                <div className="space-y-2">
                                    <div className="grid grid-cols-3 gap-2">
                                        {([
                                            {
                                                label: "3 meses", sublabel: "+90 días", preset: "3m", months: 3,
                                                active: "border-emerald-500 bg-emerald-100 text-emerald-800 ring-2 ring-emerald-500/30 dark:bg-emerald-950 dark:text-emerald-300",
                                                idle: "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400",
                                                sub: { active: "text-emerald-600 dark:text-emerald-500", idle: "text-emerald-500/60 dark:text-emerald-600" },
                                            },
                                            {
                                                label: "6 meses", sublabel: "+180 días", preset: "6m", months: 6,
                                                active: "border-blue-500 bg-blue-100 text-blue-800 ring-2 ring-blue-500/30 dark:bg-blue-950 dark:text-blue-300",
                                                idle: "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400",
                                                sub: { active: "text-blue-600 dark:text-blue-500", idle: "text-blue-500/60 dark:text-blue-600" },
                                            },
                                            {
                                                label: "1 año", sublabel: "+365 días", preset: "1y", months: 12,
                                                active: "border-violet-500 bg-violet-100 text-violet-800 ring-2 ring-violet-500/30 dark:bg-violet-950 dark:text-violet-300",
                                                idle: "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-400",
                                                sub: { active: "text-violet-600 dark:text-violet-500", idle: "text-violet-500/60 dark:text-violet-600" },
                                            },
                                        ] as const).map(({ label, sublabel, preset, months, active, idle, sub }) => (
                                            <button
                                                key={preset}
                                                type="button"
                                                onClick={() => {
                                                    const d = new Date()
                                                    d.setMonth(d.getMonth() + months)
                                                    setField("nextMaintenanceDate", d.toISOString().split("T")[0])
                                                    setMaintenancePreset(preset)
                                                }}
                                                className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-md border font-semibold tracking-wide transition-all cursor-pointer ${maintenancePreset === preset ? active : idle}`}
                                            >
                                                <span className="text-xs">{label}</span>
                                                <span className={`text-[10px] font-normal tracking-normal ${maintenancePreset === preset ? sub.active : sub.idle}`}>
                                                    {sublabel}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                    <DatePicker
                                        value={report.nextMaintenanceDate}
                                        onChange={v => {
                                            setField("nextMaintenanceDate", v)
                                            setMaintenancePreset(null)
                                        }}
                                        placeholder="Fecha personalizada..."
                                        disablePast
                                    />
                                </div>
                            </FormField>
                            <div className="flex items-center gap-3">
                                <Switch checked={report.requiresRemoteFollow} onCheckedChange={v => setField("requiresRemoteFollow", v)} className="cursor-pointer" />
                                <Label className="text-sm cursor-pointer">Requiere seguimiento remoto posterior al servicio</Label>
                            </div>
                        </SectionCard>

                        {/* 8. Cierre */}
                        <SectionCard step={8} title="Conformidad del cliente" description="Responsables y adjuntos del informe">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <FormField label="Nombre del responsable">
                                    <Input value={report.clientResponsible} onChange={e => setField("clientResponsible", e.target.value)} />
                                </FormField>
                                <FormField label="Cargo del responsable">
                                    <Input placeholder="Ej: Jefe de Laboratorio" value={report.clientResponsiblePosition} onChange={e => setField("clientResponsiblePosition", e.target.value)} />
                                </FormField>
                                <FormField label="Observaciones del Cliente">
                                    <Textarea placeholder="Comentarios del cliente sobre el servicio realizado..."
                                        value={report.clientObservations} onChange={e => setField("clientObservations", e.target.value)}
                                        rows={3} className="resize-none" />
                                </FormField>
                            </div>
                            <Separator className="opacity-40" />
                            <div className="flex items-center gap-3">
                                <Switch checked={report.deliveredOperative} onCheckedChange={v => setField("deliveredOperative", v)} className="cursor-pointer" />
                                <Label className="text-sm cursor-pointer">Equipo entregado en estado operativo al cliente</Label>
                            </div>
                            <Separator className="opacity-40" />
                            <GenericListManager
                                items={report.documentReferences}
                                onChange={v => setField("documentReferences", v)}
                                label="Referencias de Documentos"
                                placeholder="Ej: CERT-2024-001, PROT-DQ-023..."
                                emptyMessage="Sin referencias registradas"
                                addButtonText="Agregar referencia"
                                singularLabel="referencia"
                                pluralLabel="referencias"
                            />
                            <Separator className="opacity-40" />

                            <Separator className="opacity-40" />
                            <FormField label="Notas internas">
                                <Textarea
                                    placeholder="Notas internas..."
                                    value={report.internalNotes}
                                    onChange={e => setField("internalNotes", e.target.value)}
                                    rows={3}
                                    className="  focus-visible:ring-amber-500/30"
                                />
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    Este campo es solo para uso interno y no aparece en el informe generado.
                                </p>
                            </FormField>

                            <Separator className="opacity-40" />

                            <FormField label="Anexos">
                                {(() => {
                                    const parsed = report.idAttachments.map(parseAttachment)
                                    return (
                                        <ReportFilesUploader
                                            files={pendingFiles}
                                            onChange={setPendingFiles}
                                            existingFileIds={parsed.map(a => a.id)}
                                            existingDescriptions={parsed.map(a => a.description)}
                                            onRemoveExisting={removeExistingAttachment}
                                            onUpdateExistingDescription={updateExistingDescription}
                                        />
                                    )
                                })()}
                            </FormField>
                        </SectionCard>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 flex-wrap pb-6 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                className="cursor-pointer"
                                onClick={() => {
                                    if (!hasUnsavedChanges) {
                                        navigate({ to: '/instruments/$id', params: { id: instrument?.$id ?? "" } })
                                    } else {
                                        setShowCancelDialog(true)
                                    }
                                }}
                            >
                                Atras
                            </Button>
                            <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>¿Salir sin guardar?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            Los cambios que no hayas guardado como borrador se perderán. Puedes guardar un borrador antes de salir para no perder el progreso.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter className="mt-2">
                                        <AlertDialogCancel className="cursor-pointer">
                                            Seguir editando
                                        </AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={() => navigate({ to: '/instruments/$id', params: { id: instrument?.$id ?? "" } })}
                                            className="cursor-pointer bg-destructive hover:bg-destructive/90 text-white"
                                        >
                                            Salir sin guardar
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                            <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={savingDraft}
                                className="cursor-pointer text-amber-700 border-amber-300 hover:bg-amber-50">
                                {savingDraft ? "Guardando..." : revisionMode ? "Guardar borrador de revisión" : "Guardar borrador"}
                            </Button>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button type="button" disabled={saving} className="cursor-pointer gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
                                        <CheckCircle2 className="h-4 w-4" />
                                        {saving
                                            ? "Guardando..."
                                            : revisionMode
                                                ? `Finalizar revisión REV${revisionMode.nextRevisionNumber}`
                                                : "Finalizar informe"}
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <div className="flex items-center gap-3 mb-1">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                                                <ShieldAlert className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <AlertDialogTitle className="text-lg">
                                                {revisionMode
                                                    ? `¿Finalizar revisión REV${revisionMode.nextRevisionNumber}?`
                                                    : "¿Finalizar informe?"}
                                            </AlertDialogTitle>
                                        </div>
                                        <AlertDialogDescription className="text-sm leading-relaxed">
                                            Una vez finalizado, el informe quedará registrado de forma permanente y
                                            no podrá ser modificado. Si necesitas hacer cambios deberás crear una revisión.
                                            ¿Estás seguro de que deseas continuar?
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter className="mt-2">
                                        <AlertDialogCancel className="cursor-pointer gap-1.5">
                                            <X className="h-4 w-4" />
                                            Cancelar
                                        </AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={handleSubmit}
                                            className="cursor-pointer gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
                                        >
                                            <CheckCircle2 className="h-4 w-4" />
                                            Sí, finalizar
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>

                    </div>
                </div>
            </div>
        </>
    )
}

export default AddServiceReport